/**
 * Resources / Documentation page
 */

import React from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

const documentationSections = [
  {
    title: 'Getting Started',
    icon: '🚀',
    description: 'Learn the basics of VoxaPilot',
    articles: [
      {title: 'Quick Start Guide', href: '/docs/quickstart'},
      {title: 'Core Concepts', href: '/docs/concepts'},
      {title: 'AI Models', href: '/docs/models'},
    ],
  },
  {
    title: 'Features',
    icon: '✨',
    description: 'Explore all VoxaPilot features',
    articles: [
      {title: 'Transcription', href: '/docs/features/transcription'},
      {title: 'AI Summaries', href: '/docs/features/summaries'},
      {title: 'Speaker Detection', href: '/docs/features/speaker-detection'},
      {title: 'Analytics', href: '/docs/features/analytics'},
    ],
  },
  {
    title: 'Integrations',
    icon: '🔗',
    description: 'Connect with your favorite tools',
    articles: [
      {title: 'Zoom Integration', href: '/docs/integrations/zoom'},
      {title: 'Google Meet Setup', href: '/docs/integrations/google-meet'},
      {title: 'Microsoft Teams', href: '/docs/integrations/teams'},
      {title: 'Slack Integration', href: '/docs/integrations/slack'},
    ],
  },
  {
    title: 'API Reference',
    icon: '💻',
    description: 'Build with our API',
    articles: [
      {title: 'API Overview', href: '/docs/api/overview'},
      {title: 'Authentication', href: '/docs/api/authentication'},
      {title: 'Endpoints', href: '/docs/api/endpoints'},
    ],
  },
  {
    title: 'Configuration',
    icon: '⚙️',
    description: 'Configure VoxaPilot',
    articles: [
      {title: 'Settings', href: '/docs/configuration/settings'},
      {title: 'Privacy & Security', href: ROUTES.SECURITY},
    ],
  },
  {
    title: 'Troubleshooting',
    icon: '🔧',
    description: 'Common issues and solutions',
    articles: [
      {title: 'Help Center', href: ROUTES.HELP_CENTER},
      {title: 'Contact Support', href: ROUTES.CONTACT},
    ],
  },
];

const quickLinks = [
  {title: 'Documentation', href: '/docs', description: 'Complete documentation and guides'},
  {title: 'API Reference', href: '/docs/api/overview', description: 'Complete API reference'},
  {title: 'Help Center', href: ROUTES.HELP_CENTER, description: 'Get help and support'},
  {title: 'Security', href: ROUTES.SECURITY, description: 'Learn about our security'},
];

export function ResourcesPage() {
  const {ref: headerRef, isVisible: headerVisible} = useScrollAnimation();
  const {ref: quickLinksRef, isVisible: quickLinksVisible} = useScrollAnimation();
  const {ref: docsRef, isVisible: docsVisible} = useScrollAnimation();
  const {ref: ctaRef, isVisible: ctaVisible} = useScrollAnimation();

  return (
    <LandingLayout>
      <Section padding="lg" background="white" className="pt-32 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <SectionHeader
              title="Documentation & Resources"
              subtitle="Everything you need to get the most out of VoxaPilot"
              centered
              size="lg"
            />
          </div>

          {/* Quick Links */}
          <div ref={quickLinksRef as React.RefObject<HTMLDivElement>} className="mb-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickLinks.map((link, index) => (
                <div
                  key={link.title}
                  className={`transition-all duration-700 ${
                    quickLinksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${index * 0.1}s`}}
                >
                  <Card hover className="p-6">
                    <Link to={link.href} className="block">
                      <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC] mb-2">{link.title}</h3>
                      <p className="text-sm text-textSecondary dark:text-[#858585]">{link.description}</p>
                    </Link>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Documentation Sections */}
          <div ref={docsRef as React.RefObject<HTMLDivElement>} className="mb-16">
            <h2
              className={`text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-6 transition-all duration-700 ${
                docsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Browse Documentation
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documentationSections.map((section, index) => {
                const {ref, isVisible} = useScrollAnimation();
                return (
                  <div
                    key={section.title}
                    ref={ref as React.RefObject<HTMLDivElement>}
                    className={`transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{transitionDelay: `${index * 0.1}s`}}
                  >
                    <Card hover className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{section.icon}</span>
                          <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC]">{section.title}</h3>
                        </div>
                        <p className="text-sm text-textSecondary dark:text-[#858585]">{section.description}</p>
                        <ul className="space-y-2">
                          {section.articles.map((article) => (
                            <li key={article.title}>
                              <Link
                                to={article.href}
                                className="text-sm text-textSecondary dark:text-[#858585] hover:text-gray-900 dark:hover:text-[#CCCCCC] transition-colors"
                              >
                                {article.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div
            ref={ctaRef as React.RefObject<HTMLDivElement>}
            className={`text-center bg-gray-50 dark:bg-[#252526] rounded-2xl p-12 transition-all duration-700 ${
              ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Need More Help?
            </h2>
            <p className="text-textSecondary dark:text-[#858585] mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/docs">
                <Button variant="primary" size="lg">
                  Browse Full Documentation
                </Button>
              </Link>
              <Link to={ROUTES.HELP_CENTER}>
                <Button variant="outline" size="lg">
                  Visit Help Center
                </Button>
              </Link>
              <Link to={ROUTES.CONTACT}>
                <Button variant="outline" size="lg">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

